import { useState, useCallback } from 'react';
import { authAPI } from '@/api/endpoints';
import { useAuthStore } from '@/store/authStore';
import { useTelegram } from './useTelegram';
import type { AuthResponse } from '@/api/types';

interface UseAuthReturn {
  isLoading: boolean;
  error: string | null;
  login: (email: string) => Promise<boolean>;
  register: (email: string) => Promise<boolean>;
  confirmOTP: (email: string, code: string) => Promise<AuthResponse | null>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
}

export const useAuth = (): UseAuthReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user, setUser, setTokens, setPendingEmail, clearAuth } = useAuthStore();
  const { notificationOccurred, impactOccurred } = useTelegram();

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const login = useCallback(
    async (email: string): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        await authAPI.login({ email });
        setPendingEmail(email);
        impactOccurred('medium');
        notificationOccurred('success');
        return true;
      } catch (err: unknown) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Failed to send OTP. Please try again.';
        setError(errorMessage);
        notificationOccurred('error');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [setPendingEmail, impactOccurred, notificationOccurred]
  );

  const register = useCallback(
    async (email: string): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        await authAPI.register({ email });
        setPendingEmail(email);
        impactOccurred('medium');
        notificationOccurred('success');
        return true;
      } catch (err: unknown) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Registration failed. Please try again.';
        setError(errorMessage);
        notificationOccurred('error');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [setPendingEmail, impactOccurred, notificationOccurred]
  );

  const confirmOTP = useCallback(
    async (email: string, code: string): Promise<AuthResponse | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await authAPI.confirmOTP({ email, code });

        // Save user and tokens
        setUser(response);
        setTokens(response.accessToken, response.refreshToken);

        impactOccurred('heavy');
        notificationOccurred('success');

        return response;
      } catch (err: unknown) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Invalid OTP code. Please try again.';
        setError(errorMessage);
        notificationOccurred('error');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [setUser, setTokens, impactOccurred, notificationOccurred]
  );

  const logout = useCallback(async (): Promise<void> => {
    setIsLoading(true);

    try {
      await authAPI.logout();
      impactOccurred('medium');
      notificationOccurred('success');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Always clear local auth state
      clearAuth();
      setIsLoading(false);
    }
  }, [clearAuth, impactOccurred, notificationOccurred]);

  // Refresh user data from server (to get updated subscription info)
  const refreshUser = useCallback(async (): Promise<void> => {
    if (!user?.id) return;

    try {
      const updatedUser = await authAPI.getAccount(user.id);
      setUser(updatedUser);
    } catch (err) {
      console.error('Failed to refresh user data:', err);
    }
  }, [user?.id, setUser]);

  return {
    isLoading,
    error,
    login,
    register,
    confirmOTP,
    logout,
    refreshUser,
    clearError,
  };
};
